"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { Comment, CommentFormData, CommentCategory } from "@/lib/types";
import CommentForm from "@/components/CommentForm";
import CommentItem from "@/components/CommentItem";
import { getCommentsLikeDataAction, toggleCommentLikeAction } from "@/lib/actions/commentLikes";
import { createComment, editComment, getComments, removeComment } from "@/lib/actions/comments";
import { useCommentDeepLink } from "@/hooks/useCommentDeepLink";
import { COMMENT_CATEGORY_ORDER, toCommentTabs } from "@/lib/comment-category";

const COMMENTS_PER_PAGE = 10;

// 이 섹션이 탭으로 노출하는 카테고리(순서 포함) — 댓글 딥링크 대상 카테고리 허용 목록이기도 하다 (JCG-3603)
const COMMENT_TAB_KEYS: readonly CommentCategory[] = COMMENT_CATEGORY_ORDER;

// 탭 설정 — 이모지·라벨은 comment-category.ts SSOT
const COMMENT_TABS = toCommentTabs(COMMENT_TAB_KEYS);

// 루트 댓글의 탭 카테고리. 탭 필터와 딥링크 계획이 같은 기본값("chat")을 쓰도록 한 곳에 둔다 (JCG-3603)
function resolveCommentCategory(comment: Comment): CommentCategory {
  return (comment.category || "chat") as CommentCategory;
}

// 모든 댓글 ID를 재귀적으로 추출하는 헬퍼 함수 (컴포넌트 외부 - immutability 규칙 회피)
function getAllCommentIds(comments: Comment[]): string[] {
  return comments.flatMap((comment) => [
    comment.id,
    ...(comment.replies ? getAllCommentIds(comment.replies) : []),
  ]);
}

// 댓글에 좋아요 수를 주입하는 헬퍼 함수 (컴포넌트 외부)
function injectLikeCounts(
  comments: Comment[],
  likeCounts: Record<string, number>
): Comment[] {
  return comments.map((comment) => ({
    ...comment,
    likeCount: likeCounts[comment.id] ?? 0,
    replies: comment.replies
      ? injectLikeCounts(comment.replies, likeCounts)
      : [],
  }));
}

// 댓글이 가장 많은 탭을 찾는 헬퍼 함수 (컴포넌트 외부)
function findMostPopularTab(comments: Comment[]): CommentCategory {
  const counts: Record<CommentCategory, number> = {
    chat: 0,
    suggestion: 0,
    question: 0,
    bug: 0,
  };
  comments.forEach((comment) => {
    const category = (comment.category || "chat") as CommentCategory;
    if (category in counts) {
      counts[category]++;
    }
  });

  // 가장 댓글이 많은 탭 찾기 (동점일 경우 COMMENT_TABS 순서 우선)
  let maxCount = 0;
  let mostPopularTab: CommentCategory = "chat";
  for (const tab of COMMENT_TABS) {
    if (counts[tab.key] > maxCount) {
      maxCount = counts[tab.key];
      mostPopularTab = tab.key;
    }
  }
  return mostPopularTab;
}

interface CommentSectionProps {
  slug: string; // 블로그 포스트 식별자
}

/**
 * 댓글 시스템 메인 컴포넌트
 * 댓글 목록 관리, 새 댓글 작성, 답글/수정/삭제 기능 통합
 */
export default function CommentSection({ slug }: CommentSectionProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<CommentCategory>("chat");
  const [visibleCount, setVisibleCount] = useState(COMMENTS_PER_PAGE);

  // 현재 탭에 해당하는 댓글만 필터링
  const filteredComments = useMemo(() => {
    return comments.filter((comment) => resolveCommentCategory(comment) === activeTab);
  }, [comments, activeTab]);

  // 페이지네이션 적용
  const visibleComments = useMemo(() => {
    return filteredComments.slice(0, visibleCount);
  }, [filteredComments, visibleCount]);

  const hasMore = filteredComments.length > visibleCount;

  // 각 탭별 댓글 수 계산
  const commentCounts = useMemo(() => {
    const counts: Record<CommentCategory, number> = {
      chat: 0,
      suggestion: 0,
      question: 0,
      bug: 0,
    };
    comments.forEach((comment) => {
      const category = (comment.category || "chat") as CommentCategory;
      if (category in counts) {
        counts[category]++;
      }
    });
    return counts;
  }, [comments]);

  // 탭을 댓글 수가 많은 순으로 정렬
  const sortedTabs = useMemo(() => {
    return [...COMMENT_TABS].sort((a, b) => commentCounts[b.key] - commentCounts[a.key]);
  }, [commentCounts]);

  // 댓글 딥링크(#comment-{id}, JCG-3603) — 로드 완료·해시 변경 시 대상 루트의 탭을 열고 visibleCount 를
  // 늘린 뒤 앵커로 스크롤·강조한다. loadComments 의 setActiveTab(findMostPopularTab) 커밋 뒤에 실행되므로
  // 딥링크 탭이 자동 선택 탭을 덮는다.
  const { targetCommentId: deepLinkTargetId, highlightedCommentId } = useCommentDeepLink({
    comments,
    loading,
    pageSize: COMMENTS_PER_PAGE,
    resolveCategory: resolveCommentCategory,
    allowedCategories: COMMENT_TAB_KEYS,
    activeTab,
    setActiveTab,
    setVisibleCount,
  });

  // 댓글 로드 함수 (Supabase에서 실제 데이터 로드)
  const loadComments = useCallback(async () => {
    try {
      const result = await getComments(slug);
      if (result.success) {
        const allIds = getAllCommentIds(result.comments);

        // 좋아요 데이터 로드
        if (allIds.length > 0) {
          const likeData = await getCommentsLikeDataAction(allIds);
          if (likeData.success) {
            const commentsWithLikes = injectLikeCounts(
              result.comments,
              likeData.likeCounts
            );
            setComments(commentsWithLikes);
            setLikedCommentIds(new Set(likeData.likedIds));
            // 댓글이 가장 많은 탭으로 자동 전환
            setActiveTab(findMostPopularTab(commentsWithLikes));
          } else {
            setComments(result.comments);
            setActiveTab(findMostPopularTab(result.comments));
          }
        } else {
          setComments(result.comments);
          setActiveTab(findMostPopularTab(result.comments));
        }
      } else {
        console.error("댓글 로드 실패:", result.error);
        setComments([]);
      }
    } catch (error) {
      console.error("댓글 로드 실패:", error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // 컴포넌트 마운트 시 댓글 로드 — 외부 데이터 fetch, JCG-2034 마이그레이션 예정
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadComments();
  }, [loadComments]);

  // 새 댓글 추가 함수
  const handleAddComment = async (data: CommentFormData) => {
    // 현재 활성화된 탭의 카테고리를 데이터에 추가
    const commentData: CommentFormData = {
      ...data,
      category: activeTab,
    };

    // 임시 댓글 ID 생성
    const tempId = `temp_${Date.now()}`;
    const tempComment: Comment = {
      id: tempId,
      content: commentData.content,
      author: commentData.author,
      email: commentData.email,
      createdAt: new Date().toISOString(),
      replies: [],
      category: activeTab,
    };

    try {
      // 낙관적 업데이트: 최신순이므로 맨 앞에 추가
      setComments((prev) => [tempComment, ...prev]);

      const result = await createComment(slug, commentData);

      if (result.success && result.comment) {
        // 성공 시 임시 댓글을 실제 댓글로 교체
        setComments((prev) =>
          prev.map((comment) =>
            comment.id === tempId
              ? { ...result.comment!, replies: [] }
              : comment
          )
        );
        alert("댓글이 등록되었습니다.");
        return true;
      } else {
        // 실패 시 임시 댓글 제거
        setComments((prev) => prev.filter((comment) => comment.id !== tempId));
        alert(result.error || "댓글 등록에 실패했습니다.");
        return false;
      }
    } catch (error) {
      // 에러 시 임시 댓글 제거
      setComments((prev) => prev.filter((comment) => comment.id !== tempId));
      console.error("댓글 추가 실패:", error);
      alert("댓글 등록에 실패했습니다.");
      return false;
    }
  };

  // 재귀적으로 댓글을 찾아서 답글을 추가하는 헬퍼 함수
  const addReplyToComment = (
    comments: Comment[],
    parentId: string,
    newReply: Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === parentId) {
        // 부모 댓글을 찾으면 답글 추가
        return { ...comment, replies: [...(comment.replies || []), newReply] };
      } else if (comment.replies && comment.replies.length > 0) {
        // 답글 목록에서 재귀적으로 검색
        return {
          ...comment,
          replies: addReplyToComment(comment.replies, parentId, newReply),
        };
      }
      return comment;
    });
  };

  // 재귀적으로 댓글을 찾아서 임시 답글을 실제 답글로 교체하는 헬퍼 함수
  const replaceReplyInComment = (
    comments: Comment[],
    tempId: string,
    newReply: Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === tempId) {
        // 직접 일치하는 댓글을 찾으면 교체
        return { ...newReply, replies: comment.replies || [] };
      } else if (comment.replies && comment.replies.length > 0) {
        // 답글 목록에서 재귀적으로 검색하고 교체
        return {
          ...comment,
          replies: replaceReplyInComment(comment.replies, tempId, newReply),
        };
      }
      return comment;
    });
  };

  // 재귀적으로 댓글을 찾아서 임시 답글을 제거하는 헬퍼 함수
  const removeReplyFromComment = (
    comments: Comment[],
    tempId: string
  ): Comment[] => {
    return comments
      .map((comment) => ({
        ...comment,
        replies: comment.replies
          ? removeReplyFromComment(comment.replies, tempId).filter(
              (reply) => reply.id !== tempId
            )
          : [],
      }))
      .filter((comment) => comment.id !== tempId);
  };

  // 재귀적으로 댓글을 찾아서 수정하는 헬퍼 함수
  const updateCommentInComments = (
    comments: Comment[],
    commentId: string,
    updateFn: (comment: Comment) => Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === commentId) {
        // 해당 댓글을 찾으면 수정
        return updateFn(comment);
      } else if (comment.replies && comment.replies.length > 0) {
        // 답글 목록에서 재귀적으로 검색하고 수정
        return {
          ...comment,
          replies: updateCommentInComments(
            comment.replies,
            commentId,
            updateFn
          ),
        };
      }
      return comment;
    });
  };

  // 답글 추가 함수
  const handleAddReply = async (parentId: string, data: CommentFormData) => {
    // 현재 활성화된 탭의 카테고리를 데이터에 추가
    const replyData: CommentFormData = {
      ...data,
      category: activeTab,
    };

    // 임시 답글 ID 생성
    const tempId = `temp_${Date.now()}`;
    const tempReply: Comment = {
      id: tempId,
      content: replyData.content,
      author: replyData.author,
      email: replyData.email,
      createdAt: new Date().toISOString(),
      parentId: parentId,
      replies: [],
      category: activeTab,
    };

    try {
      // 낙관적 업데이트: 즉시 UI에 반영 (재귀적 업데이트 사용)
      setComments((prev) => addReplyToComment(prev, parentId, tempReply));

      const result = await createComment(slug, replyData, parentId);

      if (result.success && result.comment) {
        // 성공 시 임시 답글을 실제 답글로 교체 (재귀적 교체 사용)
        setComments((prev) =>
          replaceReplyInComment(prev, tempId, result.comment!)
        );
        alert("답글이 등록되었습니다.");
        return true;
      } else {
        // 실패 시 임시 답글 제거 (재귀적 제거 사용)
        setComments((prev) => removeReplyFromComment(prev, tempId));
        alert(result.error || "답글 등록에 실패했습니다.");
        return false;
      }
    } catch (error) {
      // 에러 시 임시 답글 제거 (재귀적 제거 사용)
      setComments((prev) => removeReplyFromComment(prev, tempId));
      console.error("답글 추가 실패:", error);
      alert("답글 등록에 실패했습니다.");
      return false;
    }
  };

  // 댓글 수정 함수
  const handleEditComment = async (
    commentId: string,
    email: string,
    data: CommentFormData
  ) => {
    // 원본 댓글 백업
    const originalComments = [...comments];

    try {
      // 낙관적 업데이트: 즉시 UI에 반영 (재귀적 수정 사용)
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (comment) => ({
          ...comment,
          content: data.content,
          author: data.author,
        }))
      );

      const result = await editComment(commentId, email, data, slug);

      if (result.success && result.comment) {
        // 성공 시 서버 데이터로 최종 업데이트 (재귀적 수정 사용)
        // likeCount는 서버 응답에 포함되지 않으므로 기존 값 보존
        setComments((prev) =>
          updateCommentInComments(prev, commentId, (comment) => ({
            ...result.comment!,
            replies: comment.replies || [],
            likeCount: comment.likeCount,
          }))
        );
        alert("댓글이 수정되었습니다.");
        return true;
      } else {
        // 실패 시 원본으로 롤백
        setComments(originalComments);
        alert(result.error || "댓글 수정에 실패했습니다.");
        return false;
      }
    } catch (error) {
      // 에러 시 원본으로 롤백
      setComments(originalComments);
      console.error("댓글 수정 실패:", error);
      alert("댓글 수정에 실패했습니다.");
      return false;
    }
  };

  // 댓글 삭제 함수
  const handleDeleteComment = async (commentId: string, email: string) => {
    // 원본 댓글 백업
    const originalComments = [...comments];

    try {
      // 낙관적 업데이트: 소프트 삭제로 마킹 (재귀적 수정 사용)
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (comment) => ({
          ...comment,
          author: null,
          content: null,
          isDeleted: true,
        }))
      );

      const result = await removeComment(commentId, email, slug);

      if (result.success) {
        alert("댓글이 삭제되었습니다.");
      } else {
        // 실패 시 원본으로 롤백
        setComments(originalComments);
        alert(result.error || "댓글 삭제에 실패했습니다.");
      }
    } catch (error) {
      // 에러 시 원본으로 롤백
      setComments(originalComments);
      console.error("댓글 삭제 실패:", error);
      alert("댓글 삭제에 실패했습니다.");
    }
  };

  // 댓글 좋아요 핸들러
  const handleLikeComment = async (commentId: string) => {
    // 낙관적 업데이트
    const wasLiked = likedCommentIds.has(commentId);
    
    // 좋아요 상태 토글
    setLikedCommentIds((prev) => {
      const newSet = new Set(prev);
      if (wasLiked) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });

    // 좋아요 수 업데이트
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (comment) => ({
        ...comment,
        likeCount: (comment.likeCount ?? 0) + (wasLiked ? -1 : 1),
      }))
    );

    try {
      const result = await toggleCommentLikeAction(commentId);
      
      if (result.success) {
        // 서버 응답으로 정확한 값 업데이트
        setLikedCommentIds((prev) => {
          const newSet = new Set(prev);
          if (result.liked) {
            newSet.add(commentId);
          } else {
            newSet.delete(commentId);
          }
          return newSet;
        });
        
        setComments((prev) =>
          updateCommentInComments(prev, commentId, (comment) => ({
            ...comment,
            likeCount: result.likeCount,
          }))
        );
      } else {
        // 실패 시 롤백
        setLikedCommentIds((prev) => {
          const newSet = new Set(prev);
          if (wasLiked) {
            newSet.add(commentId);
          } else {
            newSet.delete(commentId);
          }
          return newSet;
        });
        
        setComments((prev) =>
          updateCommentInComments(prev, commentId, (comment) => ({
            ...comment,
            likeCount: (comment.likeCount ?? 0) + (wasLiked ? 1 : -1),
          }))
        );
      }
    } catch (error) {
      // 에러 시 롤백
      console.error("좋아요 처리 실패:", error);
      setLikedCommentIds((prev) => {
        const newSet = new Set(prev);
        if (wasLiked) {
          newSet.add(commentId);
        } else {
          newSet.delete(commentId);
        }
        return newSet;
      });
      
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (comment) => ({
          ...comment,
          likeCount: (comment.likeCount ?? 0) + (wasLiked ? 1 : -1),
        }))
      );
    }
  };

  if (loading) {
    return (
      <div className="mt-8 mx-2 sm:mx-0">
        <div className="border-t border-[0.5px] border-gray-200 dark:border-neutral-400 pt-8">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#706E6E] dark:border-neutral-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 mx-2 sm:mx-0">
      {/* 댓글 섹션 헤더 */}
      <div className="rounded-md border-[0.5px] dark:border-neutral-400 pt-8 p-3 sm:p-4">
        <h2 className="border-b-[0.5px] border-gray-200 dark:border-neutral-400 pb-2 text-xl sm:text-2xl font-semibold text-[#333] dark:text-neutral-200 mb-2">
          댓글
        </h2>

        {/* 탭 네비게이션 */}
        <div className="flex gap-1 sm:gap-2 mb-4 mt-4 overflow-x-auto">
          {sortedTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setVisibleCount(COMMENTS_PER_PAGE); }}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-4 py-2 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-[#333] dark:bg-neutral-200 text-white dark:text-neutral-800"
                  : "bg-gray-100 dark:bg-neutral-700 text-[#706E6E] dark:text-neutral-300 hover:bg-gray-200 dark:hover:bg-neutral-600"
              }`}
            >
              <span>{tab.emoji}</span>
              <span className="hidden sm:inline">{tab.label}</span>
              <span
                className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key
                    ? "bg-white/20 text-white dark:bg-neutral-800/30 dark:text-neutral-800"
                    : "bg-gray-200 dark:bg-neutral-600 text-[#706E6E] dark:text-neutral-300"
                }`}
              >
                {commentCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* 댓글 목록 */}
        <div className="space-y-0">
          {visibleComments.length > 0 ? (
            visibleComments.map((comment, index) => {
              const total = visibleComments.length;
              // 최신순 정렬이므로 가장 최신 2개(index 0, 1)만 truncate 제외
              const shouldTruncate = index >= 2;

              return (
                <div key={comment.id}>
                  <CommentItem
                    comment={comment}
                    onReply={handleAddReply}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                    onLike={handleLikeComment}
                    likedCommentIds={likedCommentIds}
                    shouldTruncate={shouldTruncate}
                    deepLinkTargetId={deepLinkTargetId}
                    highlightedCommentId={highlightedCommentId}
                  />
                  {index < total - 1 && (
                    <hr className="border-gray-200 dark:border-neutral-600" />
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-[#706E6E] dark:text-gray-400 font-light">
              {activeTab === "chat" && "아직 잡담이 없습니다. 자유롭게 이야기를 나눠보세요!"}
              {activeTab === "question" && "아직 질문이 없습니다. 문제에 대한 궁금한 점을 물어보세요!"}
              {activeTab === "bug" && "아직 버그 리포트가 없습니다. 오류를 발견하면 알려주세요!"}
              {activeTab === "suggestion" && "아직 제안이 없습니다. 개선 아이디어를 공유해주세요!"}
            </div>
          )}
        </div>

        {/* 더보기 버튼 */}
        {hasMore && (
          <div className="flex justify-center py-4">
            <button
              onClick={() => setVisibleCount((prev) => prev + COMMENTS_PER_PAGE)}
              className="px-6 py-2 text-sm font-medium text-[#706E6E] dark:text-neutral-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-full transition-colors"
            >
              이전 댓글 더보기 ({filteredComments.length - visibleCount}개 남음)
            </button>
          </div>
        )}

        {/* 새 댓글 작성 폼 */}
        <div className="mb-8 p-3 sm:p-4 mt-2 border-[0.5px] border-gray-200 dark:border-neutral-400 bg-[#FAFAFA] dark:bg-gray-800 rounded-md">
          <h3 className="text-base sm:text-lg font-semibold text-[#333] dark:text-neutral-200 mb-4">
            {activeTab === "chat" && "💬 잡담 작성"}
            {activeTab === "question" && "❓ 질문하기"}
            {activeTab === "bug" && "🐛 버그 리포트"}
            {activeTab === "suggestion" && "💡 제안하기"}
          </h3>
          <CommentForm onSubmit={handleAddComment} />
        </div>
      </div>
    </div>
  );
}
